/** @jsx isolateComponent */

import { Fragment } from 'react';

import { AsyncIndicator, FlexLayout } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Link } from '@zapier/design-system';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import { EVENT_COMPANY_TRIAL_BANNER_UPGRADE } from '@zapier/design-system-revenue/src/constants';
import {
  buildUpgradeUrl,
  useSize,
} from '@zapier/design-system-revenue/src/utils';

import { useData as _useData } from './hooks/useData';
import {
  Props as ShouldRenderProps,
  useShouldRender,
} from './hooks/useShouldRender';

type Props = {
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

const CompanyTrialBanner = ({ useData = _useData }: Props) => {
  const {
    accountId,
    currentPlan,
    error,
    isAccountOwner,
    isLoading,
    nextPlan,
    profileId,
    trialDaysRemaining,
  } = useData();

  const { trackEvent } = useTrackingContext();
  const size = useSize();

  if (isLoading) {
    return <AsyncIndicator />;
  }

  if (error) {
    return <Fragment>{error}</Fragment>;
  }

  if (!currentPlan || !nextPlan) {
    return null;
  }

  const onUpgradeClick = () => {
    trackEvent(EVENT_COMPANY_TRIAL_BANNER_UPGRADE);
  };

  const onAutomationExpertClick = () => {
    trackEvent('company-trial-guide', {
      accountId,
      profileId,
      isOwner: isAccountOwner,
    });
  };

  return (
    <FlexLayout justify={size === 'large' ? 'space-between' : 'center'}>
      <div>
        You have{' '}
        <strong>
          {trialDaysRemaining} day{trialDaysRemaining !== 1 && <span>s</span>}{' '}
          remaining
        </strong>{' '}
        of your Zapier for Companies trial.{' '}
        <Link
          color="white"
          href={buildUpgradeUrl(
            currentPlan.name,
            nextPlan.name,
            nextPlan.interval
          )}
          onClick={onUpgradeClick}
        >
          <span style={{ textDecoration: 'underline' }}>Upgrade now.</span>
        </Link>
      </div>

      <Link
        color="white"
        href="/app/company-account-setup/getting-started"
        onClick={onAutomationExpertClick}
      >
        <span style={{ textDecoration: 'underline' }}>
          Meet with Automation Expert
        </span>
      </Link>
    </FlexLayout>
  );
};

CompanyTrialBanner.shouldRender = function shouldRender(
  props: ShouldRenderProps = {}
) {
  return useShouldRender.call(this, props);
};

export { CompanyTrialBanner };
